import axios from 'axios';

/**
 * Which entry shape the installation uses — the environment-level half of D18.
 *
 * The SPA cannot read this off the hostname. `nexo-test.example.com` has the same
 * shape as `tenant1.nexo.com`, so a shape test classifies every custom domain as a
 * tenant subdomain and suppresses the `__tenant` hint — which is what routes the
 * login in an environment that has no origin template. Getting it wrong is not a
 * degraded experience either way: suppress the hint where it was needed and no
 * tenant user can sign in at all; send it from a real tenant subdomain and the auth
 * host rejects the flow before creating it.
 *
 * So the installation states it, once per load, and both answers are cached.
 */
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:5050';

interface TenantEntryConfig {
  subdomainEntry: boolean;
}

/** null = not answered yet (or the request failed); callers fall back to the hostname. */
let subdomainEntry: boolean | null = null;
let inFlight: Promise<boolean | null> | null = null;

/**
 * The cached answer, for synchronous callers (`isTenantBoundHostname`, the login
 * screen's render). null means "not known here" — the caller keeps the hostname-shape
 * behaviour, which is the conservative reading: a real tenant subdomain keeps working,
 * and a neutral domain is no worse off than before. An authorize request never relies on
 * it being filled: `authService.tenantQueryParam` loads it first.
 */
export function subdomainEntryEnabled(): boolean | null {
  return subdomainEntry;
}

/**
 * Fetch it once per load. Concurrent callers share the in-flight request, so the
 * boot path and the login screen cost one request between them.
 */
export async function loadTenantEntryConfig(): Promise<boolean | null> {
  if (subdomainEntry !== null) return subdomainEntry;

  if (!inFlight) {
    inFlight = axios
      .get<TenantEntryConfig>(`${API_BASE_URL}/api/tenant-entry/config`)
      .then((response) => {
        subdomainEntry = response.data?.subdomainEntry === true;
        return subdomainEntry;
      })
      .catch((error) => {
        // Not fatal on its own: the login round is about to talk to the same API, so
        // an unreachable one fails visibly a moment later either way.
        console.warn('Tenant entry configuration lookup failed', error);
        return null;
      })
      .finally(() => {
        inFlight = null;
      });
  }

  return inFlight;
}

/** Test seam: the module-level cache is per page load in the browser. */
export function __setSubdomainEntryForTests(value: boolean | null): void {
  subdomainEntry = value;
  inFlight = null;
}
